import { IIntervalService, IRootScopeService, ITimeoutService } from 'angular';
import { Cesium } from 'app/cesium';
import { Asset } from 'app/shared/collections/asset';
import { AssetCollection } from 'app/shared/collections/asset_collection.service';
import { ImsObjectCollectionOptions } from 'app/shared/collections/ims_object_collection.service';
import { SimpleFetcher } from 'app/shared/collections/time/fetcher/simple_fetcher.factory';
import { LocationProcessor } from 'app/shared/collections/time/processor/location_processor.factory';
import { TimeIntervalChunker } from 'app/shared/collections/time/queue/time_interval_chunker.factory';
import { ClockHandler } from 'app/shared/time/clock_handler.service';
import { adjustHeightOfCartesian } from 'app/shared/utilities/mapping_utils';
import { julianToUnix } from 'app/shared/utilities/time';
import { ViewStateService } from 'app/shared/view_state.service';
import { isNil } from 'lodash';
import { LocationStatsResource } from '../resources';
import { SiteStateService } from '../site_state.service';
import { LocationStats } from './location_stat';
import { ImsTimeCollection } from './time/ims_time_collection.factory';
import { StartupRegistry } from 'app/startup/registry/registry';
import { ConfigService } from 'app/config/config.service';
import { ILocalStorageService } from 'angular-local-storage';

export function getAssetHeight(asset: Asset): number {
    const assetHeight = asset?.height;
    if (!isNil(assetHeight)) {
        return assetHeight;
    }
    const assetTypeHeight = asset?.type?.height;
    if (!isNil(assetTypeHeight)) {
        return assetTypeHeight;
    }
    return 0;
}

export class LocationStatCollection extends ImsTimeCollection {
    private readonly _locationProcessor: LocationProcessor;
    private readonly _assetCollection: AssetCollection;

    constructor(
        startUpRegistry: StartupRegistry,
        localStorageService: ILocalStorageService,
        $rootScope: IRootScopeService,
        $timeout: ITimeoutService,
        $interval: IIntervalService,
        clockHandler: ClockHandler,
        viewStateService: ViewStateService,
        siteStateService: SiteStateService,
        locationProcessor: LocationProcessor,
        assetCollection: AssetCollection,
        options?: ImsObjectCollectionOptions,
    ) {
        super(
            startUpRegistry,
            localStorageService,
            $rootScope,
            $timeout,
            $interval,
            clockHandler,
            viewStateService,
            siteStateService,
            LocationStatsResource,
            LocationStats,
            new TimeIntervalChunker(ConfigService.timeIntervalChunkLocationStats),
            new SimpleFetcher(),
            locationProcessor,
            Object.assign(
                {
                    requestLatestStats: true,
                },
                options,
            ),
        );

        this._locationProcessor = locationProcessor;
        this._assetCollection = assetCollection;
    }

    getLatestStatsResourceFunction() {
        return this.resourceObjectBase.latest_by_asset;
    }

    extendParams(params) {
        return params;
    }

    getPositionCount(assetId: number): number {
        return this._locationProcessor.getCountOfPositions(assetId);
    }

    /**
     * Get the interpolated position of the given asset for the given time in float Unix seconds
     * @param assetId
     * @param time
     * @param applyAssetHeight
     */
    getPositionAtUnixTime(
        assetId: number,
        time: number,
        applyAssetHeight: boolean = true,
    ): Cesium.Cartesian3 {
        const position = this._locationProcessor.getAssetPositionUnix(assetId, time);
        if (position && applyAssetHeight) {
            const asset = this._assetCollection.get(assetId);
            const height = getAssetHeight(asset);
            return adjustHeightOfCartesian(position, height);
        }

        return position;
    }

    /**
     * Get the interpolated position of the given asset for the given time as a JulianDate
     * @param assetId
     * @param time
     * @param applyAssetHeight
     */
    getPositionAtTime(
        assetId: number,
        time: Cesium.JulianDate,
        applyAssetHeight: boolean = true,
    ): Cesium.Cartesian3 {
        const unix = julianToUnix(time);
        return this.getPositionAtUnixTime(assetId, unix, applyAssetHeight);
    }

    /**
     * Get the closest (past) LocationStat of the given asset for the given time as a JulianDate
     * @param assetId
     * @param time
     */
    getLocationStatAtTime(assetId: number, time: Cesium.JulianDate): LocationStats | null {
        const unix = julianToUnix(time);
        return this._locationProcessor.get(assetId, unix)[0];
    }
}
